import * as s from "@gromia/components/theme/t.module.css";
import { useMedia } from "@gromia/utils/useMedia";
import cx from "classnames";
import React, { MouseEventHandler } from "react";
import Slick from "react-slick";

type Props = {
  children: NonNullable<React.ReactNode>;
  dark?: boolean;
  options?: Partial<{
    arrows: boolean;
    dots: boolean;
    dotsSuperimpose: boolean;
    autoplay: boolean;

    perPageXl: number;
    perPageLg: number;
    perPageMd: number;
    perPageSm: number;
    perPageXs: number;
  }>;
};

export const Carousel = (props: Props) => {
  const options = {
    autoplay: false,
    arrows: true,
    dots: true,
    dotsSuperimpose: false,
    perPageXl: 4,
    perPageLg: 4,
    perPageMd: 3,
    perPageSm: 2,
    perPageXs: 1,
    ...props.options,
  };

  const media = useMedia();

  const count = media.xl
    ? options.perPageXl
    : media.lg
    ? options.perPageLg
    : media.md
    ? options.perPageMd
    : media.sm
    ? options.perPageSm
    : options.perPageXs;

  const settings = {
    dots: options.dots,
    infinite: true,
    arrows: options.arrows,
    autoplay: options.autoplay,
    speed: 500,
    autoplaySpeed: 5000,
    slidesToShow: count,
    slidesToScroll: count,
    customPaging: (i: number) => <button aria-label={`Page ${i}`} />,
    nextArrow: <Next />,
    prevArrow: <Prev />,
  };

  return (
    <Slick
      {...settings}
      className={cx(
        options.dotsSuperimpose ? "slick-superimpose-dots" : null,
        props.dark ? "slick-slider-dark" : null,
      )}
    >
      {props.children}
    </Slick>
  );
};

Carousel.Pad = (props: { children: React.ReactNode }) => {
  return <div className={cx(s.m2)}>{props.children}</div>;
};

Carousel.Card = (props: { children: React.ReactNode; className?: string }) => {
  return (
    <div
      className={cx(
        s.dFlex,
        s.h100,
        s.m2,
        s.p4,
        s.bgWhite,
        s.shadow,
        s.rounded,
        props.className,
      )}
    >
      {props.children}
    </div>
  );
};

type ButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

function Next(props: ButtonProps) {
  return (
    <button
      role="none"
      aria-label="Next"
      className="slick-arrow slick-next"
      data-role="none"
      onClick={props.onClick}
    />
  );
}

function Prev(props: ButtonProps) {
  return (
    <button
      role="none"
      aria-label="Prev"
      className="slick-arrow slick-prev"
      data-role="none"
      onClick={props.onClick}
    />
  );
}
